/**
 * Finance API Types & Schemas
 * Defines DTOs, Zod schemas for validation, and API endpoints
 */

import { z } from '@ufc/core'

// ===========================================
// Zod Schemas
// ===========================================

// Base schemas
export const moneyDtoSchema = z.object({
  amount: z.number(),
  currency: z.string().length(3), // ISO 4217 (e.g., "SAR", "USD")
})

export const transactionTypeDtoSchema = z.enum([
  'payment',
  'invoice',
  'credit',
  'debit',
])

export const transactionStatusDtoSchema = z.enum([
  'completed',
  'pending',
  'failed',
  'processing',
])

export const paymentMethodDtoSchema = z.enum([
  'bank_transfer',
  'credit_card',
  'cash',
  'check',
  'viban',
  'sadad',
])

// Transaction DTO schema
export const transactionDtoSchema = z.object({
  id: z.string().uuid(),
  transactionNumber: z.string(),
  type: transactionTypeDtoSchema,
  status: transactionStatusDtoSchema,
  amount: moneyDtoSchema,
  description: z.string(),
  reference: z.string().optional(),
  date: z.string().datetime(),
  dueDate: z.string().datetime().optional(),
  paidDate: z.string().datetime().optional(),
  paymentMethod: paymentMethodDtoSchema.optional(),
  relatedOrderId: z.string().optional(),
  relatedSalesOrderId: z.string().optional(),
  relatedOrderNumber: z.string().optional(),
  balanceAfter: moneyDtoSchema.optional(),
  createdAt: z.string().datetime(),
})

// Payment DTO schema
export const paymentDtoSchema = z.object({
  id: z.string().uuid(),
  paymentNumber: z.string(),
  amount: moneyDtoSchema,
  status: transactionStatusDtoSchema,
  method: paymentMethodDtoSchema,
  date: z.string().datetime(),
  invoiceIds: z.array(z.string().uuid()),
  reference: z.string().optional(),
  notes: z.string().optional(),
  createdAt: z.string().datetime(),
})

// Statement DTO schema
export const statementDtoSchema = z.object({
  periodStart: z.string().datetime(),
  periodEnd: z.string().datetime(),
  openingBalance: moneyDtoSchema,
  closingBalance: moneyDtoSchema,
  totalCredits: moneyDtoSchema,
  totalDebits: moneyDtoSchema,
  transactions: z.array(transactionDtoSchema),
})

// Finance stats DTO schema
export const financeStatsDtoSchema = z.object({
  totalReceivable: moneyDtoSchema,
  totalPayable: moneyDtoSchema,
  pendingPayments: z.number().int().nonnegative(),
  overdueInvoices: z.number().int().nonnegative(),
})

// ===========================================
// Request Schemas
// ===========================================

// Transaction list query
export const transactionListQuerySchema = z.object({
  type: transactionTypeDtoSchema.optional(),
  status: transactionStatusDtoSchema.optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
  search: z.string().optional(),
  orderId: z.string().optional(),
  salesOrderId: z.string().optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().max(100).optional(),
})

// Create transaction request
export const createTransactionRequestSchema = z.object({
  type: transactionTypeDtoSchema,
  amount: moneyDtoSchema,
  description: z.string().min(1),
  reference: z.string().optional(),
  date: z.string().datetime(),
  dueDate: z.string().datetime().optional(),
  paymentMethod: paymentMethodDtoSchema.optional(),
  relatedOrderId: z.string().optional(),
  relatedSalesOrderId: z.string().optional(),
})

// Update transaction request
export const updateTransactionRequestSchema = z.object({
  status: transactionStatusDtoSchema.optional(),
  description: z.string().min(1).optional(),
  reference: z.string().optional(),
  paidDate: z.string().datetime().optional(),
  paymentMethod: paymentMethodDtoSchema.optional(),
})

// Create payment request
export const createPaymentRequestSchema = z.object({
  amount: moneyDtoSchema,
  method: paymentMethodDtoSchema,
  date: z.string().datetime(),
  invoiceIds: z.array(z.string().uuid()).min(1),
  reference: z.string().optional(),
  notes: z.string().optional(),
})

// Statement query
export const statementQuerySchema = z.object({
  periodStart: z.string().datetime(),
  periodEnd: z.string().datetime(),
})

// ===========================================
// Response Schemas
// ===========================================

export const transactionListResponseSchema = z.object({
  data: z.array(transactionDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
})

export const paymentListResponseSchema = z.object({
  data: z.array(paymentDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
})

// ===========================================
// Inferred Types
// ===========================================

export type MoneyDto = z.infer<typeof moneyDtoSchema>
export type TransactionTypeDto = z.infer<typeof transactionTypeDtoSchema>
export type TransactionStatusDto = z.infer<typeof transactionStatusDtoSchema>
export type PaymentMethodDto = z.infer<typeof paymentMethodDtoSchema>

export type TransactionDto = z.infer<typeof transactionDtoSchema>
export type PaymentDto = z.infer<typeof paymentDtoSchema>
export type StatementDto = z.infer<typeof statementDtoSchema>
export type FinanceStatsDto = z.infer<typeof financeStatsDtoSchema>

export type TransactionListQuery = z.infer<typeof transactionListQuerySchema>
export type CreateTransactionRequest = z.infer<typeof createTransactionRequestSchema>
export type UpdateTransactionRequest = z.infer<typeof updateTransactionRequestSchema>
export type CreatePaymentRequest = z.infer<typeof createPaymentRequestSchema>
export type StatementQuery = z.infer<typeof statementQuerySchema>

export type TransactionListResponse = z.infer<typeof transactionListResponseSchema>
export type PaymentListResponse = z.infer<typeof paymentListResponseSchema>

// ===========================================
// Validation Helpers
// ===========================================

export function validateTransactionDto(data: unknown): TransactionDto {
  return transactionDtoSchema.parse(data)
}

export function validatePaymentDto(data: unknown): PaymentDto {
  return paymentDtoSchema.parse(data)
}

export function validateStatementDto(data: unknown): StatementDto {
  return statementDtoSchema.parse(data)
}

export function validateFinanceStatsDto(data: unknown): FinanceStatsDto {
  return financeStatsDtoSchema.parse(data)
}

export function validateTransactionListResponse(data: unknown): TransactionListResponse {
  return transactionListResponseSchema.parse(data)
}

export function validatePaymentListResponse(data: unknown): PaymentListResponse {
  return paymentListResponseSchema.parse(data)
}

// ===========================================
// API Endpoints
// ===========================================

export const FINANCE_ENDPOINTS = {
  // Transactions
  TRANSACTIONS_LIST: '/api/v1/finance/transactions',
  TRANSACTION_DETAIL: (id: string) => `/api/v1/finance/transactions/${id}`,
  TRANSACTION_CREATE: '/api/v1/finance/transactions',
  TRANSACTION_UPDATE: (id: string) => `/api/v1/finance/transactions/${id}`,
  TRANSACTION_DELETE: (id: string) => `/api/v1/finance/transactions/${id}`,

  // Payments
  PAYMENTS_LIST: '/api/v1/finance/payments',
  PAYMENT_DETAIL: (id: string) => `/api/v1/finance/payments/${id}`,
  PAYMENT_CREATE: '/api/v1/finance/payments',
  PAYMENT_UPDATE: (id: string) => `/api/v1/finance/payments/${id}`,
  PAYMENT_DELETE: (id: string) => `/api/v1/finance/payments/${id}`,

  // Statements
  STATEMENT: '/api/v1/finance/statement',
  STATEMENT_DOWNLOAD: '/api/v1/finance/statement/download',

  // Stats
  STATS: '/api/v1/finance/stats',
} as const
